import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import emailjs from '@emailjs/browser';

const initialFormData = {
  // Section 1: Personal Information
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dateOfBirth: '',

  // Section 2: Certifications & Education
  primaryCertification: '',
  additionalCertifications: '',
  cprCertification: '',

  // Section 3: Experience & Specialties
  yearsExperience: ''
};

const TrainerApplicationModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.primaryCertification) newErrors.primaryCertification = 'Primary certification is required';
    if (!formData.yearsExperience) newErrors.yearsExperience = 'Years of experience is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // EmailJS service configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Check if credentials are loaded
      if (!serviceId || !templateId || !publicKey) {
        alert('EmailJS configuration is missing. Please check your .env file.');
        setSubmitting(false);
        return;
      }

      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        date_of_birth: formData.dateOfBirth,
        primary_certification: formData.primaryCertification,
        additional_certifications: formData.additionalCertifications,
        cpr_certification: formData.cprCertification,
        years_experience: formData.yearsExperience,
        message: Object.entries(formData)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('\n'),
        to_email: 'Varsityfitnesslb@outlook.com'
      };

      await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      alert('Application submitted successfully!');
      onClose();
      setFormData(initialFormData);
    } catch (error) {
      alert('Failed to submit application. Please try again.');
      console.error('EmailJS error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormContent = () => {
    return (
          <Box>
            {/* Section 1 */}
            <Typography variant="h6" sx={{ mb: 3, color: theme.palette.primary.main }}>
              Section 1 — Personal Information
            </Typography>

            {/* Row 1: Name fields */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  sx={{ minWidth: 400 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  sx={{ minWidth: 400 }}
                  required
                />
              </Grid>
            </Grid>

            {/* Row 2: Phone and Date of Birth */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 400 }}
                  label="Phone Number"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 400 }}
                  label="Date of Birth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            {/* Row 3: Email (full width) */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 816 }}
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 2 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 2 — Certifications & Education
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.primaryCertification} sx={{ minWidth: 300 }}>
                  <InputLabel>Personal Training Certification *</InputLabel>
                  <Select
                    value={formData.primaryCertification}
                    onChange={(e) => handleInputChange('primaryCertification', e.target.value)}
                  >
                    <MenuItem value="NASM">NASM</MenuItem>
                    <MenuItem value="ACE">ACE</MenuItem>
                    <MenuItem value="ISSA">ISSA</MenuItem>
                    <MenuItem value="NSCA">NSCA</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {errors.primaryCertification && <Typography variant="caption" color="error">{errors.primaryCertification}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Additional Certifications"
                  value={formData.additionalCertifications}
                  onChange={(e) => handleInputChange('additionalCertifications', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="CPR/AED Certification – Provider & Expiration Date"
                  value={formData.cprCertification}
                  onChange={(e) => handleInputChange('cprCertification', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 3 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 3 — Experience & Specialties
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.yearsExperience}  sx={{ minWidth: 400 }}>
                  <InputLabel>Years of Personal Training Experience *</InputLabel>
                  <Select
                    value={formData.yearsExperience}
                    onChange={(e) => handleInputChange('yearsExperience', e.target.value)}
                  >
                    <MenuItem value="0–1">0–1</MenuItem>
                    <MenuItem value="1–3">1–3</MenuItem>
                    <MenuItem value="3–5">3–5</MenuItem>
                    <MenuItem value="5+">5+</MenuItem>
                  </Select>
                  {errors.yearsExperience && <Typography variant="caption" color="error">{errors.yearsExperience}</Typography>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
        Varsity Fitness – New Trainer Application
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {renderFormContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainerApplicationModal;
